import { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import siteContent from '../../data/siteContent.json';

const Navbar = () => {
    const { general } = siteContent;
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { name: 'Ana Sayfa', href: '#home' },
        { name: 'Hakkımızda', href: '#about' },
        { name: 'Hizmetler', href: '#services' },
        { name: 'Süreç', href: '#process' },
        { name: 'Galeri', href: '#gallery' },
        { name: 'İletişim', href: '#contact' },
    ];

    const scrollToId = (targetId) => {
        const element = document.getElementById(targetId);
        if (element) {
            const offset = 80;
            const elementPosition = element.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
                top: elementPosition - offset,
                behavior: 'smooth'
            });
        }
    };

    const handleNavClick = (e, href) => {
        e.preventDefault();
        const targetId = href.replace('#', '');

        // Close menu first for mobile, then scroll in next frame to avoid animation interference
        setIsMobileMenuOpen(false);

        if (location.pathname === '/') {
            requestAnimationFrame(() => scrollToId(targetId));
        } else {
            navigate('/');
            setTimeout(() => scrollToId(targetId), 300);
        }
    };

    return (
        <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-md py-4' : 'bg-primary-charcoal/40 backdrop-blur-[2px] py-6'}`}>
            <div className="container mx-auto px-4 md:px-6 flex justify-between items-center">
                {/* Logo */}
                <a
                    href="#home"
                    onClick={(e) => handleNavClick(e, '#home')}
                    className="flex items-center gap-2 group"
                >
                    <img
                        src="/assets/logos/ERDE-256.png"
                        srcSet="/assets/logos/ERDE-256.png 256w, /assets/logos/ERDE-512.png 512w"
                        sizes="(min-width: 768px) 80px, 64px"
                        alt="Erde Mobilya Logo"
                        width="80"
                        height="80"
                        loading="eager"
                        decoding="async"
                        className={`w-16 h-16 md:w-20 md:h-20 object-contain transition-all duration-300 ${!isScrolled ? 'brightness-0 invert' : ''}`}
                    />
                    <div className="flex flex-col">
                        <span className={`font-heading font-bold text-lg md:text-xl tracking-wider leading-none transition-colors duration-300
              ${isScrolled ? 'text-primary-charcoal' : 'text-white'}`}>
                            ERDE
                        </span>
                        <span className={`text-[10px] md:text-xs tracking-[0.2em] font-light transition-colors duration-300
              ${isScrolled ? 'text-primary-walnut' : 'text-white/80'}`}>
                            MOBİLYA & TASARIM
                        </span>
                    </div>
                </a>

                {/* Desktop Menu */}
                <div className="hidden md:flex items-center gap-8">
                    {navLinks.map((link) => (
                        <a
                            key={link.name}
                            href={link.href}
                            onClick={(e) => handleNavClick(e, link.href)}
                            className={`text-sm font-medium uppercase tracking-wide hover:text-accent-gold transition-colors duration-300 relative group cursor-pointer
                ${isScrolled ? 'text-primary-charcoal' : 'text-white'}`}
                        >
                            {link.name}
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent-gold transition-all duration-300 group-hover:w-full"></span>
                        </a>
                    ))}
                    <a
                        href={`tel:${general.contact.phone.replace(/\s+/g, '')}`}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-300
               ${isScrolled
                                ? 'border-primary-charcoal text-primary-charcoal hover:bg-primary-charcoal hover:text-white'
                                : 'border-white/50 text-white hover:bg-white hover:text-primary-charcoal'}`}
                    >
                        <Phone size={16} />
                        <span className="text-xs font-bold">{general.contact.phone}</span>
                    </a>
                </div>

                {/* Mobile Menu Button */}
                <button
                    type="button"
                    className={`md:hidden p-2 transition-colors ${isScrolled ? 'text-primary-charcoal' : 'text-white'}`}
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    aria-label={isMobileMenuOpen ? 'Menüyü kapat' : 'Menüyü aç'}
                    aria-expanded={isMobileMenuOpen}
                    aria-controls="mobile-menu"
                >
                    {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                </button>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        id="mobile-menu"
                        className="md:hidden bg-white shadow-xl overflow-hidden"
                    >
                        <div className="flex flex-col p-6 gap-4">
                            {navLinks.map((link) => (
                                <a
                                    key={link.name}
                                    href={link.href}
                                    className="text-primary-charcoal font-heading font-semibold text-lg hover:text-accent-gold transition-colors block"
                                    onClick={(e) => handleNavClick(e, link.href)}
                                >
                                    {link.name}
                                </a>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navbar;
